import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../fonts/breuer-headline.css";
import "./wand.css";
import { setBackButtonUrl } from "../uiSlice";
import { getHouseById } from "../house/housesConfig";
import { getCharacterByIds } from "../character/charactersConfig";
import { WANDS } from "./wandsConfig";
import selectBtnImg from "../images/select.png";
import paperBg from "../images/paper.png";

const HOGWARTS_SELECTION_KEY_BASE = "hogwartsSelectionData";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function getSelectionStorageKey(userLike) {
  const stored = readStoredUserData();
  const uid = userLike?.userId ?? userLike?.userid ?? userLike?.id ?? stored?.userId ?? stored?.userid ?? stored?.id;
  const sid = userLike?.sessionId ?? stored?.sessionId;
  const oid = userLike?.organizationId ?? stored?.organizationId;
  const scope = [uid, sid, oid]
    .map((v) => String(v ?? "").trim())
    .join(":");
  return scope.replace(/:/g, "") ? `${HOGWARTS_SELECTION_KEY_BASE}:${scope}` : HOGWARTS_SELECTION_KEY_BASE;
}

function readSelectionArray(userLike) {
  try {
    const raw = localStorage.getItem(getSelectionStorageKey(userLike));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function findSelectionEntry(type, userLike) {
  return readSelectionArray(userLike).find((item) => item?.type === type) ?? null;
}

function upsertSelectionEntry(type, value, userLike) {
  const current = readSelectionArray(userLike);
  const filtered = current.filter((item) => item?.type !== type);
  filtered.push({ type, ...value });
  try {
    localStorage.setItem(getSelectionStorageKey(userLike), JSON.stringify(filtered));
  } catch {
    // ignore storage write failures
  }
}

function Wand() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, user } = useSelector((state) => state.auth);
  const selectedHouse = findSelectionEntry("house", user);
  const selectedCharacter = findSelectionEntry("character", user);
  const { houseId: navHouseId, characterId: navCharacterId, characterName: navCharacterName } = location.state ?? {};
  const houseId = navHouseId ?? selectedHouse?.houseId;
  const characterId = navCharacterId ?? selectedCharacter?.characterId;
  const characterName = navCharacterName ?? selectedCharacter?.characterName;
  const house = houseId ? getHouseById(houseId) : null;
  const character = getCharacterByIds(houseId, characterId);
  const [shopEntered, setShopEntered] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    document.documentElement.classList.add("wand-static-bg");
    document.body.classList.add("wand-static-bg");
    return () => {
      document.documentElement.classList.remove("wand-static-bg");
      document.body.classList.remove("wand-static-bg");
    };
  }, []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/character"));
  }, [dispatch, status, user]);

  if (!house || !characterId || !character) {
    return <Navigate to="/character" replace />;
  }

  const openWandSelection = () => setShopEntered(true);

  const handleShopHitKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openWandSelection();
    }
  };

  const safeIndex = ((index % WANDS.length) + WANDS.length) % WANDS.length;
  const current = WANDS[safeIndex];

  const goPrev = () => setIndex((i) => i - 1);
  const goNext = () => setIndex((i) => i + 1);

  const handleSelect = () => {
    upsertSelectionEntry("wand", {
      wandId: current.id,
      wandName: current.name,
    }, user);
    navigate("/stage1_intro", {
      state: {
        houseId: house.id,
        characterId,
        characterName,
        wandId: current.id,
        wandName: current.name,
      },
    });
  };

  return (
    <div
      className={
        shopEntered ? "wand-page" : "wand-page wand-page--intro"
      }
    >
      {!shopEntered ? (
        <div className="wand-intro-scene">
          <div className="wand-intro-stack">
            <div className="wand-intro-character-wrap">
              <img
                src={character.portrait}
                alt={characterName ?? character.name}
                className="wand-intro-character"
              />
            </div>
            <div className="wand-intro-bubble">
              <p className="wand-intro-bubble-text">
                Enter the Wand Shop and choose the wand that will define your
                magic.
              </p>
            </div>
          </div>
          <div
            id="wand-shop-enter-hit"
            className="wand-shop-hit"
            role="button"
            tabIndex={0}
            onClick={openWandSelection}
            onKeyDown={handleShopHitKeyDown}
            aria-label="Enter the wand shop"
          />
        </div>
      ) : (
        <div className="wand-layout">
          <header className="wand-scroll">
            <p className="wand-scroll-title">
            Every Wizard Needs a Wand

            </p>
          </header>

          <div className="wand-frame">
            <div className="wand-frame-inner">
              <div
                className="wand-parchment"
                style={{ backgroundImage: `url(${paperBg})` }}
              >
                <div className="wand-carousel">
                  <div
                    className="wand-carousel-row"
                    role="group"
                    aria-label="Browse wands"
                  >
                    <button
                      type="button"
                      className="wand-arrow wand-arrow--prev"
                      onClick={goPrev}
                      aria-label="Previous wand"
                    >
                      ‹
                    </button>
                    <div className="wand-image-wrap">
                      <img
                        src={current.image}
                        alt={current.name}
                        className="wand-image"
                      />
                    </div>
                    <button
                      type="button"
                      className="wand-arrow wand-arrow--next"
                      onClick={goNext}
                      aria-label="Next wand"
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div className="wand-details">
                  <div className="wand-details-text">
                    <h2 className="wand-name">{current.name}</h2>
                    <p className="wand-desc">{current.description}</p>
                  </div>
                  <div className="wand-select-row">
                    <button
                      type="button"
                      className="wand-select-btn"
                      onClick={handleSelect}
                    >
                      <img src={selectBtnImg} alt="Select this wand" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Wand;
